import React, { useEffect, useState, Suspense, useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faList,
  faChartLine,
  // faClipboard,
  // faClipboardCheck,
  // faClipboardList,
} from "@fortawesome/free-solid-svg-icons";
import { FaRegClipboard, FaTrash } from "react-icons/fa";
import Button from "../button-v1";
import styles from "../../styles/patient-page-modules/Charts.module.css";
import AddReadingDialog from "./patient-modals/AddReadingDialog";
import CollapsableSection from "../collapsableSection";
import Table from "../table";
import DateRangePicker from "../shared-modules/DatePicker/range/index";
import Spinner from "../spinner";
// import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { formatDate, displayDate } from "../../utils/helper";
import {
  getPatientReadings,
  fetchPatientReadingEvents,
  mapReadingEvents,
  deleteReading,
  getPatientGeneralDetails,
} from "../../services/patient/details";
import Modal from "../shared-modules/Modal";
import { getUserIdentity } from "../../utils/authorization";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { buildReportFilename, DOCTYPES } from "../../utils/fileNaming";

import { bloodPressureColumns } from "./manual-reading/blood-pressure/columns";
import { bloodGlucoseColumns } from "./manual-reading/glucose/columns";
import { pulseOXColumns } from "./manual-reading/pulse-ox/columns";
import { temperatureColumns } from "./manual-reading/temperature/columns";
import { weightColumns } from "./manual-reading/weight/columns";
import Reload from "../ui/reload-data";
import HealthChart from "../shared-modules/HealthChart";
import { useNavigate } from "react-router-dom";
import { Share2, Download as DownloadIcon, MoreVertical } from "lucide-react";
import { Dropdown, DropdownItem } from "../ui/dropdown";
import sentSmsIcon from "../../assets/images/sent_sms.svg";
import { ReactComponent as ShareSvg } from "../../assets/icons/chart/chart-share.svg";
import { ReactComponent as DownloadSvg } from "../../assets/icons/chart/chart-download.svg";
import { exportPatientReadings } from "../../services/patient/documents";
import { toast } from "react-hot-toast";
import { CiFileOn, CiImageOn } from "react-icons/ci";
import { FaExpand } from "react-icons/fa";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../ui/popover";
import ThresholdTogglesPopover from "../shared-modules/health-chart/ThresholdTogglesPopover";
import { useToggle } from "../../hooks/useToggle";
import clsx from "clsx";
import Events from "../events";
import Tooltip from "../shared-modules/Tooltip";
import AlertDialog from "../alert-dialogue";

const DownloadReadingDialogue = React.lazy(() =>
  import("../demo-download-dialogue")
);

const readingTypes = [
  { value: "blood_pressure", label: "Blood Pressure" },
  { value: "blood_glucose", label: "Blood Glucose" },
  { value: "weight", label: "Weight" },
  { value: "pulse_ox", label: "Pulse Oximeter" },
  { value: "temperature", label: "Temperature" },
];

const DEVICE_NAME_TO_READING_TYPE = {
  "Tenovi BPM - L": "blood_pressure",
  "Tenovi BPM - S": "blood_pressure",
  "Tenovi Cellular BPM - L": "blood_pressure",
  "Tenovi Cellular BPM - S": "blood_pressure",
  "Omron Wrist BPM - 7 Series": "blood_pressure",
  "Tenovi Glucometer": "blood_glucose",
  "Tenovi Cellular Glucometer": "blood_glucose",
  "Tenovi Pulse Ox": "pulse_ox",
  "Tenovi Cellular Pulse Ox": "pulse_ox",
  "Tenovi Scale": "weight",
  "Tenovi Cellular Scale": "weight",
  "Bariatric Scale": "weight",
  "Tenovi Thermometer": "temperature",
};

const READING_TYPE_PRIORITY = [
  "blood_pressure",
  "blood_glucose",
  "weight",
  "pulse_ox",
  "temperature",
];

const timeFrames = [
  { value: "2days", label: "Past 2 Days" },
  { value: "1week", label: "1 Week" },
  { value: "1month", label: "1 Month" },
  { value: "3months", label: "3 Month" },
  { value: "6months", label: "6 Month" },
  { value: "1year", label: "1 Year" },
];

const shareOptions = [
  { value: "pdf", label: "PDF", icon: <CiFileOn size={16} /> },
  { value: "png", label: "PNG", icon: <CiImageOn size={16} /> },
];

const Readings = ({ patientId: propPatientId, isSectionOpen, onSectionClose, CaseManager = false, expandButton = true, compactToolbar = false, programLabel = "RPM" }) => {
  const queryClient = useQueryClient();
  const { setIsCareplanExpand } = useToggle();
  // const accountId = sessionStorage.getItem("acc_id");
  // const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const navigate = useNavigate();
  const addReadingButtonRef = useRef(null);
  const [view, setView] = useState("graph");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timeFrame, setTimeFrame] = useState("1week");
  const [readingType, setReadingType] = useState(() => {
    const pid = propPatientId || sessionStorage.getItem("_pt_id");
    const cached = queryClient.getQueryData(["patient-general-details", pid]);
    if (cached?.devices?.length) {
      const connectedTypes = new Set(
        cached.devices
          .filter((d) => d.connectivity_status !== "not_connected")
          .map((d) => DEVICE_NAME_TO_READING_TYPE[d.device_name])
          .filter(Boolean)
      );
      const defaultType = READING_TYPE_PRIORITY.find((t) => connectedTypes.has(t));
      if (defaultType) return defaultType;
    }
    return "blood_pressure";
  });
  const [dateRangeBadgeValue, setDateRangeBadgeValue] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [shareOption, setShareOption] = useState(null);

  // Use propPatientId if provided, otherwise fall back to sessionStorage
  const [patientId, setPatientId] = useState(() =>
    propPatientId || sessionStorage.getItem("_pt_id")
  );

  const hasAutoSelectedType = useRef(false);

  // Update patientId when prop changes (for dailies navigation)
  useEffect(() => {
    if (propPatientId) {
      setPatientId(propPatientId);
      hasAutoSelectedType.current = false;
    }
  }, [propPatientId]);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [readingToDelete, setReadingToDelete] = useState(null);

  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });

  // May 2026 redesign: threshold visibility state lifted from HealthChart
  // so the "Set Reading Thresholds" popover (rendered in this component's
  // chart panel header) can drive the same state HealthChart reads to
  // display dashed reference lines on the chart.
  const [thresholds, setThresholds] = useState({
    systolicLow: false,
    systolicHigh: false,
    diastolicLow: false,
    diastolicHigh: false,
    high: false,
    low: false,
    oxygenLow: false,
  });
  const toggleThreshold = (key) => {
    setThresholds((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const [thresholdsPopoverOpen, setThresholdsPopoverOpen] = useState(false);
  // Threshold defaults per reading type — matches the CONFIG constant in
  // HealthChart.js so the popover can label rows with the threshold value.
  const THRESHOLD_VALUES_BY_TYPE = {
    blood_pressure: {
      systolicHigh: 140,
      systolicLow: 90,
      diastolicHigh: 90,
      diastolicLow: 60,
    },
    weight: { high: 100, low: 40 },
    temperature: { high: 99.5, low: 97.0 },
    pulse_ox: { oxygenLow: 95 },
    blood_glucose: { high: 140, low: 70 },
  };
  const THRESHOLD_UNITS = {
    blood_pressure: "mmHg",
    weight: "kg",
    temperature: "°F",
    pulse_ox: "%",
    blood_glucose: "mg/dL",
  };

  // const isPatient =
  //   JSON.parse(localStorage.getItem("user"))?.account_roles?.[0]?.name ===
  //   "Patient";
  const isPatient = getUserIdentity()?.isPatient;

  const { data, isLoading, error, refetch } = useQuery(
    ["patient-readings", patientId, timeFrame, readingType, dateRange],
    () => getPatientReadings(patientId, readingType, timeFrame, dateRange),
    {
      enabled: !!patientId,
      staleTime: 5 * 60 * 1000,
    }
  );
  const readings = data?.readings;
  const bp_stats = data?.bp_stats;
  const stats = data?.stats;

  const {
    data: events,
    refetch: refetchEvents
  } = useQuery(
    ["patient-reading-events", readingType, patientId],
    () => fetchPatientReadingEvents(readingType),
    {
      enabled: !!patientId && !!readingType,
      refetchOnWindowFocus: true,
      staleTime: 5 * 60 * 1000,
      select: mapReadingEvents,
      onError: () => {
        toast.error("Failed to load events");
      }
    }
  );

  const { data: patientDetails } = useQuery(
    ["patient-general-details", patientId],
    () => getPatientGeneralDetails(patientId),
    { enabled: !!patientId, staleTime: 5 * 60 * 1000 }
  );

  const assignedReadingTypes = useMemo(() => {
    if (patientDetails === undefined) return null; // still loading — show all active
    if (!patientDetails?.devices?.length) return new Set(); // loaded, no devices → all greyed
    return new Set(
      patientDetails.devices
        .filter((d) => d.connectivity_status !== "not_connected")
        .map((d) => DEVICE_NAME_TO_READING_TYPE[d.device_name])
        .filter(Boolean)
    );
  }, [patientDetails]);

  useEffect(() => {
    if (hasAutoSelectedType.current) return;
    if (!assignedReadingTypes || !assignedReadingTypes.size) return;
    const defaultType = READING_TYPE_PRIORITY.find((t) => assignedReadingTypes.has(t));
    if (defaultType) {
      hasAutoSelectedType.current = true;
      setReadingType(defaultType);
    }
  }, [assignedReadingTypes]);

  useEffect(() => {
    if (view === 'graph') {
      refetchEvents();
    }
  }, [view, refetchEvents]);

  const deleteReadingMutation = useMutation(deleteReading, {
    onSuccess: () => {
      toast.success("Reading deleted successfully");
      setIsDeleteAlertOpen(false);
      setReadingToDelete(null);
      // Invalidate queries to refresh the list
      queryClient.invalidateQueries(["patient-readings"]);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error || "Failed to delete reading");
    },
  });

  const handleDeleteReading = () => {
    if (readingToDelete) {
      deleteReadingMutation.mutate(readingToDelete);
    }
  };

  const {
    data: exportReadings,
    isLoading: isExporting,
    status: exportStatus,
  } = useQuery(
    ["export-download-readings", shareOption],
    () =>
      exportPatientReadings(
        patientId,
        shareOption,
        readingType,
        timeFrame,
        dateRange
      ),
    {
      enabled: !!shareOption,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      staleTime: 0,
      retry: false,
      onSettled: () => {
        setShareOption(null);
      },
      onSuccess: (data) => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;

        const filename = buildReportFilename({
          patientFirstName: patientDetails?.user?.first_name,
          patientLastName: patientDetails?.user?.last_name,
          doctype: DOCTYPES.REPORT,
          program: programLabel,
          ext: shareOption,
        });
        link.setAttribute("download", filename);

        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Readings exported successfully");
      },
      onError: (error) => {
        console.error("Export error:", error);
        toast.error("Could not download readings");
      },
    }
  );

  const handleRangeChange = (startDate, endDate) => {
    setDateRange({ startDate, endDate });
    setTimeFrame("");
  };

  const openModal = () => {
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = "auto";
  };

  const handleReadingTypeSelection = (value) => {
    hasAutoSelectedType.current = true;
    setReadingType(value);
  };

  const handleTimeFrameSelection = (value) => {
    setTimeFrame(value);
    setDateRange({
      startDate: "",
      endDate: "",
    });
  };

  const handleTable = () => {
    let columns;
    readingType === "blood_pressure"
      ? (columns = [...bloodPressureColumns])
      : readingType === "blood_glucose"
        ? (columns = [...bloodGlucoseColumns])
        : readingType === "pulse_ox"
          ? (columns = [...pulseOXColumns])
          : readingType === "weight"
            ? (columns = [...weightColumns])
            : (columns = [...temperatureColumns]);

    // Action Column
    columns.push({
      key: "actions",
      label: "Action",
      alignLeft: false,
      render: (row) => {
        // A reading is device-sourced if it carries ANY device identity.
        // `device` alone is unreliable: the backend leaves it null for some
        // Tenovi devices, so a device reading would look manual. A manual
        // reading has every provenance field null.
        const isManual =
          !row.device &&
          !row.hwi_device_id &&
          !row.device_name &&
          !row.hardware_uuid;

        const deleteButton = (
          <Button
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              if (isManual) {
                setReadingToDelete(row.id);
                setIsDeleteAlertOpen(true);
              }
            }}
            disabled={!isManual}
            style={{
              color: isManual ? "#dc2626" : "#a3a3a3",
              cursor: isManual ? "pointer" : "not-allowed",
              padding: "4px 8px",
              opacity: isManual ? 1 : 0.6
            }}
          >
            <FaTrash size={16} />
          </Button>
        );

        if (!isManual) {
          return (
            <Tooltip content="Only manually added readings can be deleted" position="top">
              {deleteButton}
            </Tooltip>
          );
        }

        return deleteButton;
      },
    });

    const tableColumns = columns.map((col) => {
      if (col.key !== "reading_dates") return col;

      return {
        ...col,
        render: (reading) => {
          const showSmsIcon = stats?.patient_sms_toggle === true;
          const iconColorFilter = reading?.sms_sent
            ? "invert(44%) sepia(83%) saturate(362%) hue-rotate(90deg) brightness(96%) contrast(88%)"
            : "invert(20%) sepia(94%) saturate(6971%) hue-rotate(358deg) brightness(96%) contrast(110%)";

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {showSmsIcon && (
                <img
                  src={sentSmsIcon}
                  alt={reading?.sms_sent ? "SMS sent" : "SMS not sent"}
                  title={reading?.sms_sent ? "SMS sent" : "SMS not sent"}
                  style={{
                    width: "16px",
                    height: "16px",
                    filter: iconColorFilter,
                  }}
                />
              )}
              <span>{col.render(reading)}</span>
            </div>
          );
        },
      };
    });

    return <Table columns={tableColumns} data={readings} height={420} />;
  };

  const handleShare = () => {
    setIsOpen(true);
  };

  const handleShareOptionSelection = (value) => {
    if (value == "png") {
      setShareOption(value);
    } else {
      const params = new URLSearchParams({
        ...(timeFrame && { timeFrame }),
        ...(readingType && { readingType }),
        ...(dateRange.startDate && {
          rangeStart: new Date(dateRange.startDate).toISOString().split("T")[0],
        }),

        ...(dateRange.endDate && {
          rangeEnd: new Date(dateRange.endDate).toISOString().split("T")[0],
        }),
      });

      const url = `/patient/reading-pdf?${params.toString()}`;

      window.open(url, "_blank");
    }
  };

  useEffect(() => {
    sessionStorage.setItem(
      "reading-range-stats",
      JSON.stringify({
        timeFrame,
        readingType,
        dateRange,
      })
    );
  }, [timeFrame, readingType, dateRange]);

  const handleExportReadings = async () => {

  };

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      setDateRangeBadgeValue(
        `${displayDate(dateRange.startDate)} - ${displayDate(
          dateRange.endDate
        )}`
      );
    } else {
      setDateRangeBadgeValue(null);
    }
  }, [dateRange]);
  useEffect(() => {
    const storedPercentage = sessionStorage.getItem("completion_percentage");
    if (storedPercentage) {
      setCompletionPercentage(Number.parseInt(storedPercentage));
    }
  }, []);

  // Removed old manual useEffect for loading events

  const openChartModal = () => {
    setIsChartModalOpen(true);
  };

  const closeChartModal = () => {
    setIsChartModalOpen(false);
  };

  const handleExpand = () => { };

  // The "Add Reading" button. In compact (nested-in-Details-tab) mode the
  // button moves out of the top toolbar and into the inner chart header
  // row alongside Set Reading Thresholds + Share + Download + 3-dot,
  // matching Figma 595:1545. The modal stays mounted on both paths.
  const addReadingButton = (
    <Button
      variant="ghost"
      onClick={() => openModal()}
      className={compactToolbar ? styles.addReadingBtnCompact : undefined}
      ref={addReadingButtonRef}
    >
      {compactToolbar ? (
        // Figma 595:1582 — single-string label "+ Add Reading" in DM Sans
        // Regular 12px #0d1139, no separate plus icon.
        <span>+ Add Reading</span>
      ) : (
        <>
          <img
            src="/icons/plus.svg"
            alt="categoryicon"
            style={{ width: "14px", height: "14px" }}
          />
          <span>Add Readings</span>
        </>
      )}
    </Button>
  );

  const actions = (
    <>
      {!compactToolbar && addReadingButton}

      {isModalOpen && (
        <AddReadingDialog
          isOpen={isModalOpen}
          onClose={closeModal}
          initialReadingType={readingType}
          triggerRef={addReadingButtonRef}
        />
      )}

      <Dropdown
        value={readingType}
        onChange={(value) => handleReadingTypeSelection(value)}
        trigger={
          <Button variant="ghost" style={{ border: "1px solid green" }}>
            <img
              src="/icons/category.svg"
              alt="categoryicon"
              style={{ width: "14px", height: "14px" }}
            />
            {readingTypes.find((type) => type.value === readingType)?.label}
            <img
              src="/icons/arrow-down.svg"
              alt="categoryicon"
              style={{ width: "12px", height: "12px" }}
            />
          </Button>
        }
      >
        {readingTypes.map((type) => {
          const isActive =
            assignedReadingTypes === null ||
            assignedReadingTypes.has(type.value);
          return (
            <DropdownItem key={type.value} value={type.value}>
              {!isActive ? (
                <Tooltip
                  content="No device connected"
                  position="top"
                  tooltipStyle={{ zIndex: 99999 }}
                >
                  <span style={{ color: "#9ca3af" }}>{type.label}</span>
                </Tooltip>
              ) : (
                type.label
              )}
            </DropdownItem>
          );
        })}
      </Dropdown>

      { }

      <Button variant="ghost" className={styles.dateRangeBtn}>
        <DateRangePicker
          key={timeFrame || "custom-range"}
          startDate={dateRange.startDate || null}
          endDate={dateRange.endDate || null}
          onRangeSelect={handleRangeChange}
          placeholder="Select Date Range"
        />
      </Button>

      <Dropdown
        options={timeFrames}
        value={timeFrame}
        onChange={(value) => handleTimeFrameSelection(value)}
        trigger={
          <Button variant="ghost">
            <img
              src="/icons/clock.svg"
              alt="categoryicon"
              style={{ width: "14px", height: "14px" }}
            />
            {timeFrames.find((type) => type.value === timeFrame)?.label ||
              "Time"}
            <img
              src="/icons/arrow-down.svg"
              alt="categoryicon"
              style={{ width: "12px", height: "12px" }}
            />
          </Button>
        }
      />
    </>
  );

  const content = (
    <>
      <div className={styles.bp_header}>
        <div className={styles.bp_details}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <h2 className={styles.bp_heading}>
              {readingType === "blood_pressure"
                ? "Blood Pressure"
                : readingType === "weight"
                  ? "Weight"
                  : readingType === "temperature"
                    ? "Temperature"
                    : readingType === "blood_glucose"
                      ? "Blood Glucose"
                      : "Pulse Oximetry"}
            </h2>
          </div>
          <p className={styles.bp_dates}>
            {dateRange.startDate && dateRange.endDate
              ? `${formatDate(dateRange.startDate)} - ${formatDate(
                dateRange.endDate
              )}`
              : timeFrame === "2days"
                ? "Past 2 Days"
                : timeFrame === "1week"
                  ? "Past Week"
                  : timeFrame === "1month"
                    ? "Past Month"
                    : timeFrame === "3months"
                      ? "Past 3 Months"
                      : timeFrame === "6months"
                        ? "Past 6 Months"
                        : "Past Year"}
          </p>
        </div>

        {/* May 2026 redesign: "Set Reading Thresholds" inline link opens
            a popover with the per-reading-type threshold visibility toggles.
            Replaces the legacy "Reading Thresholds" big block card. */}
        {view === "graph" && (
          <Popover
            open={thresholdsPopoverOpen}
            onOpenChange={setThresholdsPopoverOpen}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                className={styles.thresholdsLink}
                aria-label="Set reading thresholds"
              >
                Set Reading Thresholds
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" closeOnClickOutside>
              <ThresholdTogglesPopover
                readingType={readingType}
                thresholds={thresholds}
                onToggle={toggleThreshold}
                onClose={() => setThresholdsPopoverOpen(false)}
                thresholdValues={THRESHOLD_VALUES_BY_TYPE[readingType] || {}}
                unit={THRESHOLD_UNITS[readingType] || ""}
              />
            </PopoverContent>
          </Popover>
        )}

        <div className={styles.buttons_group}>
          {compactToolbar && addReadingButton}

          {view === "events" && (
            <Button
              variant="ghost"
              onClick={() => setIsEventModalOpen(true)}
              disabled={false}
            >
              <span>Add Event</span>
            </Button>
          )}

          {!isPatient && (
            <button
              type="button"
              className={styles.iconCircleBtn}
              onClick={() => handleShare()}
              disabled={readings?.length === 0}
              aria-label="Share"
              title="Share"
            >
              {compactToolbar
                ? <ShareSvg className={styles.iconCircleSvg} />
                : <Share2 size={16} />}
            </button>
          )}

          {!isPatient && (
            <Dropdown
              value={shareOption}
              onChange={(value) => handleShareOptionSelection(value)}
              trigger={
                <button
                  type="button"
                  className={styles.iconCircleBtn}
                  onClick={() => handleExportReadings()}
                  disabled={readings?.length === 0}
                  aria-label="Download"
                  title="Download"
                >
                  {compactToolbar
                    ? <DownloadSvg className={styles.iconCircleSvg} />
                    : <DownloadIcon size={16} />}
                </button>
              }
            >
              {shareOptions?.map((option) => (
                <DropdownItem
                  key={option.value}
                  value={option.value}
                  className={styles.dropdownItem}
                >
                  {option.icon}
                  {option.label}
                </DropdownItem>
              ))}
            </Dropdown>
          )}

          {/* Compact (nested-in-Details-tab) mode: collapse list/events/expand
              under a single 3-dot menu per Figma 595:1545. Legacy mode keeps
              them as visible toolbar buttons. */}
          {compactToolbar ? (
            <Dropdown
              align="right"
              onChange={(value) => {
                if (value === "list" || value === "graph" || value === "events") {
                  setView(value);
                }
              }}
              trigger={
                <button
                  type="button"
                  className={styles.iconCircleBtn}
                  aria-label="More actions"
                  title="More actions"
                >
                  <MoreVertical size={18} />
                </button>
              }
            >
              <DropdownItem value="list" className={styles.dropdownItem}>
                View as list
              </DropdownItem>
              <DropdownItem value="graph" className={styles.dropdownItem}>
                View as graph
              </DropdownItem>
              <DropdownItem value="events" className={styles.dropdownItem}>
                View as events
              </DropdownItem>
            </Dropdown>
          ) : (
            <>
              <button
                className={`${styles.viewButton} ${view === "list" ? styles.viewButtonActive : ""
                  }`}
                onClick={() => setView("list")}
              >
                <FontAwesomeIcon icon={faList} />
              </button>
              <button
                className={`${styles.viewButton} ${view === "graph" ? styles.viewButtonActive : ""
                  }`}
                onClick={() => setView("graph")}
              >
                <FontAwesomeIcon icon={faChartLine} />
              </button>

              <button
                className={`${styles.viewButton} ${view === "events" ? styles.viewButtonActive : ""
                  }`}
                onClick={() => setView("events")}
              >
                <FaRegClipboard size={16} />
              </button>

              {expandButton && (
                <button
                  className={clsx(styles.viewButton, styles.viewButton_expand)}
                  onClick={() => setIsCareplanExpand((prev) => !prev)}
                >
                  <FaExpand />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {view === "list" ? (
        <div className={styles.listView}>
          {isLoading ? (
            <Spinner />
          ) : error ? (
            <Reload message="Could not load readings" onReload={refetch} />
          ) : (
            handleTable()
          )}
        </div>
      ) : view === "graph" ? (
        <div className={styles.graphView} style={{ position: "relative" }}>
          {readings?.length > 0 && (
            <button
              onClick={openChartModal}
              className={styles.modalViewIcon}
              aria-label="Open chart in modal view"
              style={{
                position: "absolute",
                top: "10px",
                right: "20px",
                zIndex: 10,
                background: "rgba(255, 255, 255, 0.9)",
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                padding: "6px",
                cursor: "pointer",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
              </svg>
            </button>
          )}
          <HealthChart
            readingType={readingType}
            timeFrame={timeFrame}
            dateRange={dateRange}
            readings={readings}
            events={events || []}
            bpStats={bp_stats}
            glucoseStats={stats?.glucose_stats}
            pulseOxStats={stats?.pulse_ox_stats}
            temperatureStats={stats?.temperature_stats}
            weightStats={stats?.weight_stats}
            thresholds={thresholds}
            setThresholds={setThresholds}
          />
        </div>
      ) : view === "events" ? (
        <div className={styles.eventsView}>
          <Events
            isModalOpen={isEventModalOpen}
            onOpenModal={() => setIsEventModalOpen(true)}
            onCloseModal={() => setIsEventModalOpen(false)}
            events={events || []}
            setEvents={() => { refetchEvents(); }}
            readingType={readingType}
          />
        </div>
      ) : null}

      { }

      {!isPatient && (
        <Suspense fallback={<Spinner />}>
          <DownloadReadingDialogue
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            readingType={readingType}
            timeFrame={timeFrame}
            dateRange={dateRange}
          />
        </Suspense>
      )}

      { }
      <Modal
        title={`${readingTypes.find((type) => type.value === readingType)?.label
          } Chart`}
        isOpen={isChartModalOpen}
        onClose={closeChartModal}
        width="95%"
        height="90vh"
      >
        <div className={styles.graphView}>
          <HealthChart
            readingType={readingType}
            timeFrame={timeFrame}
            dateRange={dateRange}
            readings={readings}
            events={events || []}
            bpStats={bp_stats}
            glucoseStats={stats?.glucose_stats}
            pulseOxStats={stats?.pulse_ox_stats}
            temperatureStats={stats?.temperature_stats}
            weightStats={stats?.weight_stats}
            thresholds={thresholds}
            setThresholds={setThresholds}
          />
        </div>
      </Modal>

      <AlertDialog
        open={isDeleteAlertOpen}
        setOpen={setIsDeleteAlertOpen}
        title="Delete Reading"
        description="Are you sure you want to delete this reading? This action cannot be undone."
      >
        <Button variant="ghost" onClick={() => setIsDeleteAlertOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleDeleteReading}
          loading={deleteReadingMutation.isLoading}
        >
          Delete
        </Button>
      </AlertDialog>
    </>
  );

  // Compact mode (nested in the Details tab) bypasses CollapsableSection
  // entirely. Its `overflow: hidden` + fixed `comp_body` height + the legacy
  // `.graphView { height: 400px }` were collectively clipping the bottom
  // BP legend rows. The compact layout lays out top-toolbar + divider +
  // chart content as plain flex, so nothing constrains height.
  if (compactToolbar) {
    return (
      <div className={styles.compactChartCard}>
        <div className={styles.compactTopRow}>
          <h3 className={styles.compactProgramLabel}>{programLabel}</h3>
          <div className={styles.compactTopActions}>{actions}</div>
        </div>
        <div className={styles.compactDivider} />
        <div className={styles.compactBody}>{content}</div>
      </div>
    );
  }

  return (
    <CollapsableSection
      title="Charts"
      isOpen={isSectionOpen}
      onClose={onSectionClose}
      bodyHeight={CaseManager ? "750px" : "550px"}
      actions={[actions]}
      content={content}
      noCollapse={true}
    />
  );
};

export default Readings;